// Import child components
import './menu-button/delete';
import './menu-button/support';
import './options';
import './switch';

// Import dependencies
import { html, LitElement } from 'lit';
import { property } from 'lit/decorators.js';
import { repeat } from 'lit/directives/repeat.js';

// Import global utils
import { isDefined } from '@automator/shared/utils/is-defined';
import { isEmpty } from '@automator/shared/utils/is-empty';

// Import definitions
import { getItem } from '@automator/builder/definitions/items';

// eslint-disable-next-line require-jsdoc
const AutomatorBuilderItemMixin = ( superClass ) => class extends superClass {
	// Properties

	// The parent item ID (post ID)
	@property( { type: Number, attribute: 'parent-id' } ) accessor parentID;

	// The item ID (post ID)
	@property( { type: Number, attribute: 'item-id' } ) accessor id;

	// The item code. e.g. VIEWPAGE
	@property( { type: String, attribute: 'item-code' } ) accessor code;

	// The integration code. e.g. WP
	@property( { type: String, attribute: 'integration-code' } ) accessor integrationCode;

	// Whether the item is `published` (true) or `draft` (false)
	@property( { type: Boolean, attribute: 'is-item-on' } ) accessor isOn;

	// Whether it consumes an API credit
	@property( { type: Boolean, attribute: 'uses-credit' } ) accessor usesCredit;

	// The field _values_
	@property( { type: Array, attribute: false } ) accessor fields = [];

	// All the tokens, including the auto-generated ones
	@property( { type: Array, attribute: false } ) accessor tokens = [];

	// Backup data that's used when the item is missing
	@property( { type: Array, attribute: false } ) accessor backup = [];

	// Internal, but still reactive. The ID of the options code that's open
	@property( { type: String, attribute: false } ) accessor optionsOpenCode;

	// eslint-disable-next-line require-jsdoc
	render() {
		// Check if this item is defined or missing
		if ( ! isDefined( this.directoryItemData ) ) {
			return this._templateItemMissing;
		}

		return html`
			<div class="item">
				${ this._templateSentence }
				${ this._templateIcon }
				${ this._templateTags }
				${ this._templateTopRight }
				${ this._templateSwitch }
				${ this._templateOptions }
			</div>
		`;
	}

	/**
	 * Creates the template used when the item is missing and only part of the data is available
	 *
	 * @todo This is a temporary solution. Update once we start using the new recipe object
	 * @return {TemplateResult} The message displayed when the item is missing
	 */
	get _templateItemMissing() {
		return html`
			Missing ${ this.id }
		`;
	}

	/**
	 * Creates the template of the integration icon
	 *
	 * @return {TemplateResult} The icon
	 */
	get _templateIcon() {
		return html`
			<div class="item-icon">
				<uap-log-integration-icon size="24" integration-id="${ this.integrationCode }"></uap-log-integration-icon>
			</div>
		`;
	}

	/**
	 * Creates the template of the tags
	 *
	 * @return {TemplateResult} The tags section
	 */
	get _templateTags() {
		return html`
			<div class="item-tags">
				<uo-chip size="small" variant="pill" filled>
					${ UncannyAutomator._integrations[ this.integrationCode ].name }
				</uo-chip>

				<uo-chip size="small" color="transparent" variant="pill" filled>
					#${ this.id }
				</uo-chip>
			</div>
		`;
	}

	/**
	 * Creates the template that contains the quick actions of the item and the menu item
	 *
	 * @return {TemplateResult|undefined} The top-right section
	 */
	get _templateTopRight() {
		// Check if we have to render anything at all
		if ( isEmpty( this.quickActions ) && isEmpty( this.menuItems ) ) {
			return;
		}

		return html`
			<div class="item-top-right">
				${ this._templateQuickActions }
				${ this._templateMenu }
			</div>
		`;
	}

	/**
	 * Creates a template that shows some buttons that are added directly on the item for quick access
	 *
	 * @return {TemplateResult|undefined} The buttons
	 */
	get _templateQuickActions() {
		// Check if there are quick actions available
		if ( isEmpty( this.quickActions ) ) {
			return;
		}

		return html`
			<div class="item-quick-actions">
				${ repeat(
					this.quickActions,
					( item, index ) => index,
					item => item
				) }
			</div>
		`;
	}

	/**
	 * Creates the template of the menu item with the item actions (delete, support, etc.)
	 *
	 * @return {TemplateResult|undefined} The menu with the item actions, such as "Delete"
	 */
	get _templateMenu() {
		// Check if there are menu items/actions available
		if ( isEmpty( this.menuItems ) ) {
			return;
		}

		return html`
			<div class="item-menu">
				<uo-button-dropdown reverse-direction>
					<uo-button 
						slot="target" 
						color="transparent"
						size="small"
					>
						<uo-icon id="ellipsis-vertical"></uo-icon>
					</uo-button>

					${ repeat(
						this.menuItems,
						( item, index ) => index,
						item => item
					) }

				</uo-button-dropdown>
			</div>
		`;
	}

	/**
	 * Creates the template of the item sentence
	 *
	 * @return {TemplateResult} The item sentence
	 */
	get _templateSentence() {
		return html`
			<div class="item-sentence">
				<uap-builder-item-sentence
					item-id=${ this.id }
					.sentence=${ this.directoryItemData.sentence.dynamic }
					.fields=${ this.fields }

					@uap-builder-sentence-pill-click=${ event => this.optionsOpenCode = event.detail.optionsCode }
				></uap-builder-item-sentence>
			</div>
		`;
	}

	/**
	 * Creates the block that renders the options that are currently open
	 *
	 * @return {TemplateResult|undefined} The options
	 */
	get _templateOptions() {
		// If the options are not open, don't add the container at all
		if ( isEmpty( this.optionsOpenCode ) ) {
			return;
		}

		return html`
			<div class="item-options">
				<uap-builder-item-options
					parent-id=${ this.parentID }
					item-type=${ this.type }
					item-id=${ this.id }
					item-code=${ this.code }

					integration-code=${ this.integrationCode }

					options-group-code=${ this.optionsOpenCode }

					.fieldsValues=${ this.fields }
				></uap-builder-item-options>
			</div>
		`;
	}

	/**
	 * Creates the switch to enable or disable the item
	 *
	 * @return {TemplateResult} The switch field that sets post status
	 */
	get _templateSwitch() {
		return html`
			<div class="item-switch">
				<uap-builder-item-status-switch
					item-type=${ this.type }
					item-id=${ this.id }
					?is-item-on=${ this.isOn }
				></uap-builder-item-status-switch>
			</div>
		`;
	}

	/**
	 * Creates the button that deletes the item
	 *
	 * @return {TemplateResult} The button to delete the item
	 */
	get _templateMenuButtonDelete() {
		return html`
			<uap-builder-item-menu-button-delete 
				item-type=${ this.type }
				item-id=${ this.id }
			></uap-builder-item-menu-button-delete>
		`;
	}

	/**
	 * Creates the button that sends the user to the support page
	 *
	 * @return {TemplateResult} The button to send user to the support page
	 */
	get _templateMenuButtonSupport() {
		return html`
			<uap-builder-item-menu-button-support
				support-url=${ this.directoryItemData.miscellaneous.url_support }
			></uap-builder-item-menu-button-support>
		`;
	}

	/**
	 * Returns the global/directory data of this item
	 *
	 * @return {Object} The directory data of this item. This contains, for example, the fields attributes
	 */
	get directoryItemData() {
		return getItem(
			this.integrationCode,
			this.type,
			this.code
		);
	}
}

export const AutomatorBuilderItem = AutomatorBuilderItemMixin( LitElement );
